import type { OwaDate } from "owa-datetime";
import { getDate, getHours, getISOString, getMinutes, getMonth, getSeconds, getYear, owaDate, startOfDay, userDate, utcDate, } from "owa-datetime";

/**
 * Flag start/due/complete dates are floating date-times: the calendar day, and for a custom pick
 * the wall-clock time, that the user chose, encoded with a UTC offset. This is the MAPI convention
 * shared with Outlook desktop and mobile, extended to carry a time.
 *
 * Encoding the wall clock rather than the instant keeps every flag date in one frame, so the
 * server's sort order, the group header buckets and the rendered value all agree regardless of the
 * reader's time zone.
 */

/** Any explicit UTC offset, which a floating flag date must not be shifted by. */
const TRAILING_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Reads a flag date's wall-clock fields. Neutralizing the offset rather than honouring it as an
 * instant means a value the service hands back as `2026-08-21T23:45:00-07:00` still reads as
 * August 21 at 23:45, not August 22 at 06:45.
 */
export function parseFlagDate(flagDate: string): OwaDate {
    return utcDate(flagDate.replace(TRAILING_OFFSET, "Z"));
}

/**
 * Re-anchors a flag date to the user's time zone without moving its wall clock, so it can be
 * compared against, or edited alongside, dates that already live in that time zone.
 */
export function flagDateInUserTimeZone(flagDate: string): OwaDate {
    const parsed = parseFlagDate(flagDate);
    return owaDate(userDate(), getYear(parsed), getMonth(parsed), getDate(parsed), getHours(parsed), getMinutes(parsed), getSeconds(parsed));
}

/**
 * Returns the start of the flag date's calendar day in the user's time zone, so day and week
 * bucketing compares it against the user's own "today" rather than an instant in another frame.
 */
export function startOfFlagDateDay(flagDate: string): OwaDate {
    return startOfDay(flagDateInUserTimeZone(flagDate));
}

/** Encodes a date-only flag date, used by the quick flag options and CompleteDate. */
export function toFlagDayIso(date: OwaDate): string {
    return getISOString(utcDate(getYear(date), getMonth(date), getDate(date)));
}

/** Encodes a custom flag date, preserving the picked wall-clock time. */
export function toFlagDateTimeIso(date: OwaDate): string {
    return getISOString(utcDate(getYear(date), getMonth(date), getDate(date), getHours(date), getMinutes(date), getSeconds(date)));
}

/**
 * Compares two flag dates by the wall clock they encode, so neither differing ISO precision
 * between the client (`.000Z`) and the service (`Z`) nor a differing offset representation is
 * reported as a change.
 */
export function isSameFlagDate(left: string | null, right: string | null): boolean {
    if (!left || !right) {
        return !left && !right;
    }
    return parseFlagDate(left).valueOf() === parseFlagDate(right).valueOf();
}
